import { useEffect } from 'react';
import { logger } from '@/lib/logger';
import ReactGA from 'react-ga4';
import { useLocation } from 'react-router-dom';

// Initialize GA4 only once
let isInitialized = false;

export const initializeGA = () => {
  const measurementId = import.meta.env.VITE_GA_MEASUREMENT_ID;
  
  if (!measurementId || isInitialized) return;
  
  try {
    ReactGA.initialize(measurementId, {
      gtagOptions: {
        debug_mode: import.meta.env.DEV,
      },
    });
    isInitialized = true;
  } catch (error) {
    logger.error('Failed to initialize Google Analytics:', error);
  }
};

// Check if analytics cookies are accepted, and lazy-init GA if consented
const hasAnalyticsConsent = (): boolean => {
  try {
    const consent = localStorage.getItem('cookie-consent');
    if (!consent) return false;
    const parsed = JSON.parse(consent);
    if (parsed.analytics === true) {
      // Lazy init: if user already consented on a previous visit, start GA now
      if (!isInitialized) initializeGA();
      return true;
    }
    return false;
  } catch {
    return false;
  }
};

interface AnalyticsEvent {
  category: string;
  action: string;
  label?: string;
  value?: number;
}

export const useAnalytics = () => {
  const location = useLocation();

  // Track page views
  useEffect(() => {
    if (!hasAnalyticsConsent() || !isInitialized) return;

    ReactGA.send({
      hitType: 'pageview',
      page: location.pathname + location.search,
      title: document.title,
    });
  }, [location]);

  // Track custom events
  const trackEvent = (event: AnalyticsEvent) => {
    if (!hasAnalyticsConsent() || !isInitialized) return;

    ReactGA.event({
      category: event.category,
      action: event.action,
      label: event.label,
      value: event.value,
    });
  };

  // Track authentication events
  const trackAuth = (action: 'login' | 'signup' | 'logout') => {
    trackEvent({
      category: 'Authentication',
      action,
    });
  };

  // Track business events
  const trackBusiness = (
    entity: 'concert' | 'contact' | 'venue' | 'task' | 'expense' | 'contract',
    action: 'created' | 'updated' | 'deleted' | 'exported'
  ) => {
    trackEvent({
      category: 'Business',
      action: `${entity}_${action}`,
    });
  };

  // Track GDPR events
  const trackGDPR = (action: 'export_data' | 'delete_account' | 'cookie_consent') => {
    trackEvent({
      category: 'GDPR',
      action,
    });
  };

  // Track errors
  const trackError = (errorMessage: string, errorInfo?: string) => {
    trackEvent({
      category: 'Error',
      action: 'error_occurred',
      label: `${errorMessage}${errorInfo ? ` - ${errorInfo}` : ''}`,
    });
  };

  // Track Web Vitals
  const trackWebVital = (metric: { name: string; value: number; rating: string }) => {
    trackEvent({
      category: 'Web Vitals',
      action: metric.name,
      label: metric.rating,
      value: Math.round(metric.value),
    });
  };

  return {
    trackEvent,
    trackAuth,
    trackBusiness,
    trackGDPR,
    trackError,
    trackWebVital,
  };
};
